import { Platform } from 'react-native';
import { ACCENT_TEXT } from '../../styles/accentText';
import { inkOn, shadeHex } from '../../utils/colorShade';

/**
 * CORE-1791 — материал раздела сообщений по макету v3 (desktop) и Mobile Web v1.
 *
 * Источник — итоговый каскад style.css архивов (Confluence 112852998): блок v3
 * в конце переопределяет ранние итерации, `html[data-theme=light]` — светлую
 * тему, `@media(max-width:700px)` — телефон. Здесь каскад сведён в токены: в
 * каждой строке — итоговое значение, слои фона в исходном порядке (первый
 * рисуется поверх). Фон страницы сюда не входит: это общий слой CORE-1733.
 *
 * Только веб: на нативе раздел сообщений — свой экран (ChatsScreen.native).
 */
export type MessagesV3Mode = 'dark' | 'light';

export interface MessagesV3Tokens {
  /** Основной текст, вторичный, акцент подписи/глифа. */
  text: string;
  muted: string;
  /**
   * CORE-1791 (возврат QA 23.10) — вторичный текст на выбранной строке списка.
   * Подложка выбранного диалога в тёмной теме светлее стекла, и `muted` на ней
   * давал 4.35–4.58 при норме 4.5 (превью, время). Тот же тон, на шаг светлее.
   */
  activeMuted: string;
  /**
   * Деструктивное действие меню («Удалить», «Заблокировать»): красный темы,
   * в светлой — темнее до 4.6+ на карточке меню и тёплых поверхностях.
   */
  danger: string;
  /** Акцентный текст — общий токен CORE-1733 (#F0A06D / #9A421F, как в макете). */
  accent: string;
  /** Обычная граница 1px. */
  border: string;
  /** Край большой панели. */
  panelEdge: string;
  /** Заливка внутри кольца (аватар «я» в шапке макета). */
  inner: string;
  /** Стекло панели и компактная подложка иконки. */
  panelGlass: string;
  softWell: string;
  /** Плашка выпадающих поверхностей (палитра) — шапка платформы. */
  head: string;
  headBase: string;

  contactsBg: string;
  contactsShadow: string;
  threadBg: string;
  threadShadow: string;
  headerBg: string;
  headerAvatarShadow: string;
  iconShadow: string;
  iconHoverBg: string;
  iconHoverShadow: string;
  activeContactBg: string;
  activeContactShadow: string;
  activeAvatarColor: string;
  activeAvatarShadow: string;
  railSelectedShadow: string;
  avatarBg: string;
  searchBg: string;
  searchShadow: string;
  incomingBg: string;
  outgoingBg: string;
  outgoingBase: string | null;
  outgoingBorder: string;
  incomingShadow: string;
  outgoingShadow: string;
  emojiStampBg: string;
  composeAreaBg: string;
  composerBg: string;
  composerShadow: string;
  emptyIconShadow: string;
  /**
   * Подложка под инициалами аватара без фото. В тёмной теме вторичный текст на
   * тёплом тоне поверх стекла давал 4.1–4.5:1 — по ТЗ усиливается локальная
   * подложка (тёмная основа под тоном), а не прозрачность текста.
   */
  initialsBase: string | null;
  /** Непрозрачные основы стекла, когда `backdrop-filter` не поддерживается. */
  fallbackPanel: string;
  fallbackComposer: string;
  /**
   * Ошибка (отправки, загрузки) — терракотовый из статьи Live: текст и контурная
   * иконка; контур и фон — когда у отображения есть своя плашка. В светлой теме
   * цвет совпадает с акцентными подписями, поэтому ошибка отличается формой —
   * иконкой alert рядом с текстом.
   */
  error: string;
  errorBorder: string;
  errorFill: string;
}

const CONTOUR = 'linear-gradient(124.992deg,#FF7138,#FF9A62,#F6C5A7)';

const darkGlass = 'linear-gradient(135deg,rgba(255,255,255,.075),rgba(35,37,41,.48) 54%,rgba(240,160,109,.075))';
const lightGlass = 'linear-gradient(135deg,rgba(255,255,255,.78),rgba(255,251,244,.46) 54%,rgba(255,221,189,.20))';

/** Красный ошибки темы (ThemeContext `error`) и самая тёплая подложка, на которой стоит меню. */
const LIGHT_ERROR = '#E53E3E';
const LIGHT_WARMEST_UNDER_MENU = '#F3E8D8';

const dark: MessagesV3Tokens = {
  text: '#F5F0EB',
  muted: '#B4AAA1',
  activeMuted: shadeHex('#B4AAA1', 0.15),
  danger: '#FC8181',
  accent: ACCENT_TEXT,
  border: 'rgba(241,181,142,.14)',
  panelEdge: 'rgba(243,216,200,.10)',
  inner: 'rgba(33,29,35,.94)',
  panelGlass: darkGlass,
  softWell: 'linear-gradient(145deg,rgba(243,216,200,.10),rgba(255,255,255,.025) 52%,rgba(233,130,69,.055))',
  head: 'linear-gradient(120deg,rgba(255,255,255,.025),rgba(33,35,38,.85) 48%,rgba(215,183,164,.025))',
  headBase: '#212326',
  contactsBg: `radial-gradient(ellipse at 0% 0%,rgba(240,160,109,.10),transparent 58%),${darkGlass}`,
  contactsShadow: 'inset 0 1px 0 rgba(255,255,255,.075),0 12px 36px rgba(0,0,0,.045)',
  threadBg: 'radial-gradient(ellipse at 100% 0%,rgba(240,160,109,.065),transparent 44%),linear-gradient(135deg,rgba(255,255,255,.025),rgba(32,34,38,.14))',
  threadShadow: 'inset 0 1px 0 rgba(255,255,255,.055)',
  headerBg: `radial-gradient(ellipse at 100% 0%,rgba(240,160,109,.10),transparent 72%),${darkGlass}`,
  headerAvatarShadow: 'inset 0 1px 0 rgba(243,216,200,.16)',
  iconShadow: 'inset 0 1px 0 rgba(255,255,255,.065)',
  iconHoverBg: 'rgba(240,160,109,.15)',
  iconHoverShadow: 'inset 0 1px 0 rgba(243,216,200,.17)',
  activeContactBg: 'radial-gradient(ellipse at 0% 0%,rgba(240,160,109,.13),transparent 75%),linear-gradient(120deg,rgba(243,216,200,.07),rgba(233,130,69,.06))',
  activeContactShadow: 'inset 0 1px 0 rgba(243,216,200,.14)',
  activeAvatarColor: ACCENT_TEXT,
  activeAvatarShadow: 'inset 0 1px 0 rgba(243,216,200,.13)',
  railSelectedShadow: 'inset 0 1px 0 rgba(243,216,200,.12)',
  avatarBg: 'rgba(215,183,164,.13)',
  searchBg: 'linear-gradient(135deg,rgba(15,17,20,.62),rgba(35,31,30,.35))',
  searchShadow: 'inset 0 2px 6px rgba(0,0,0,.1),0 1px 0 rgba(243,216,200,.04)',
  incomingBg: 'linear-gradient(135deg,rgba(255,255,255,.065),rgba(255,255,255,.025))',
  outgoingBg: 'linear-gradient(125deg,rgba(215,183,164,.12),rgba(240,160,109,.085))',
  outgoingBase: 'rgba(37,39,42,.92)',
  outgoingBorder: 'rgba(240,160,109,.32)',
  incomingShadow: 'inset 0 1px 0 rgba(255,255,255,.045)',
  outgoingShadow: 'inset 0 1px 0 rgba(255,255,255,.045)',
  emojiStampBg: 'rgba(32,34,38,.28)',
  composeAreaBg: 'linear-gradient(0deg,rgba(32,34,38,.42),transparent)',
  composerBg: 'radial-gradient(ellipse at 100% 0%,rgba(233,130,69,.07),transparent 65%),linear-gradient(135deg,rgba(20,22,25,.88),rgba(35,31,30,.80))',
  composerShadow: 'inset 0 1px 0 rgba(243,216,200,.09)',
  emptyIconShadow: 'inset 0 1px 0 rgba(243,216,200,.13)',
  initialsBase: 'rgba(18,19,22,.42)',
  // CORE-1791 (возврат QA 04.11) — без backdrop-filter основа была светлее
  // самого стекла над тёмной страницей, и вторичный текст опускался до 4.22
  // («Личные диалоги») и 4.38 (цитата) при пороге 4.5. Темнее и плотнее — как
  // стекло выглядит на деле.
  fallbackPanel: 'rgba(28,30,33,.97)',
  fallbackComposer: 'rgba(24,26,29,.96)',
  error: '#F3B39A',
  errorBorder: 'rgba(230,146,118,.55)',
  errorFill: 'rgba(180,90,65,.08)',
};

const light: MessagesV3Tokens = {
  ...dark,
  text: '#332A25',
  muted: '#70645D',
  activeMuted: '#70645D',
  danger: inkOn(LIGHT_ERROR, LIGHT_WARMEST_UNDER_MENU, 4.6),
  accent: ACCENT_TEXT,
  border: 'rgba(168,124,76,.15)',
  panelEdge: 'rgba(168,124,76,.13)',
  inner: '#FFFDF9',
  panelGlass: lightGlass,
  softWell: 'linear-gradient(145deg,rgba(255,255,255,.86),rgba(255,245,228,.38) 52%,rgba(233,130,69,.085))',
  head: 'linear-gradient(110deg,#F7ECD9 0%,#FFFCF4 48%,#F9EFDF 100%)',
  headBase: '#FFFCF4',
  contactsBg: `radial-gradient(ellipse at 0% 0%,rgba(240,160,109,.065),transparent 58%),${lightGlass}`,
  contactsShadow: 'inset 0 1px 0 rgba(255,255,255,.8),0 10px 32px rgba(111,74,38,.025)',
  threadBg: 'radial-gradient(ellipse at 100% 0%,rgba(240,160,109,.035),transparent 50%),linear-gradient(135deg,rgba(255,255,255,.35),rgba(255,251,244,.12))',
  threadShadow: 'inset 0 1px 0 rgba(255,255,255,.65)',
  headerBg: `radial-gradient(ellipse at 100% 0%,rgba(240,160,109,.055),transparent 70%),${lightGlass}`,
  activeContactBg: 'radial-gradient(ellipse at 0% 0%,rgba(240,160,109,.11),transparent 75%),linear-gradient(120deg,rgba(255,255,255,.7),rgba(255,241,219,.3))',
  activeContactShadow: 'inset 0 1px 0 rgba(255,255,255,.8)',
  searchBg: 'linear-gradient(135deg,rgba(255,255,255,.80),rgba(255,246,231,.55))',
  searchShadow: 'inset 0 1px 3px rgba(111,64,32,.025)',
  incomingBg: 'linear-gradient(135deg,rgba(255,255,255,.94),rgba(255,253,247,.88))',
  outgoingBg: 'linear-gradient(135deg,rgba(245,232,213,.94),rgba(248,239,223,.92))',
  outgoingBase: null,
  outgoingBorder: 'rgba(184,117,66,.35)',
  outgoingShadow: 'inset 0 1px 0 rgba(255,255,255,.7)',
  emojiStampBg: 'rgba(255,253,247,.6)',
  composeAreaBg: 'linear-gradient(0deg,rgba(255,252,244,.34),transparent)',
  composerBg: 'radial-gradient(ellipse at 100% 0%,rgba(240,160,109,.05),transparent 65%),linear-gradient(135deg,rgba(255,255,255,.94),rgba(255,246,231,.84))',
  composerShadow: 'inset 0 1px 0 rgba(255,255,255,.85)',
  initialsBase: null,
  fallbackPanel: 'rgba(255,251,243,.96)',
  fallbackComposer: 'rgba(255,253,248,.98)',
  error: '#9A421F',
  errorBorder: 'rgba(154,66,31,.42)',
  errorFill: 'rgba(180,90,65,.04)',
};

export const MESSAGES_V3: Record<MessagesV3Mode, MessagesV3Tokens> = { dark, light };

/** Фирменный контур выбранности: 1px отдельным слоем с маской, не на тексте. */
export const MESSAGES_V3_CONTOUR = CONTOUR;

/**
 * Слой контура для веба: градиент виден только в рамке шириной `padding`,
 * внутренность вычитается маской. Только полные свойства маски: сокращения
 * `mask` и `-webkit-mask` сбрасывают `mask-composite`, а в Firefox
 * `-webkit-mask` — синоним `mask`, и второе сокращение затирало `exclude`:
 * градиент заливал поле ввода и выбранную строку целиком (QA 26.10, Firefox).
 * `-webkit-mask-composite` Firefox не знает — для него работает стандартное.
 */
export function contourRingWebStyle(): Record<string, string> {
  const layers = 'linear-gradient(#fff 0 0), linear-gradient(#fff 0 0)';
  const boxes = 'content-box, border-box';
  return {
    backgroundImage: CONTOUR,
    maskImage: layers, maskClip: boxes, maskOrigin: boxes, maskComposite: 'exclude',
    WebkitMaskImage: layers, WebkitMaskClip: boxes, WebkitMaskOrigin: boxes, WebkitMaskComposite: 'xor',
  };
}

/**
 * Прозрачность контура по месту (макет): выбранный диалог .8, поле ввода .65
 * (1 при фокусе внутри), аватар в шапке, навигация и пустое состояние — .65 / 1.
 */
export const CONTOUR_OPACITY = { contact: 0.8, composer: 0.65, composerFocus: 1, avatar: 0.65, rail: 0.65, emptyIcon: 1 } as const;

/** Пузырь: тот же акцентный контур цитаты, что в макете (#E98245 — не текст). */
export const QUOTE_BAR = '#E98245';
export const QUOTE_FILL = 'rgba(233,130,69,.045)';
/** Точка «в сети». */
export const PRESENCE_DOT = '#789981';
/** Кнопка отправки: градиент и глиф (#332A25 на 4.5+ ко всем стопам). */
export const SEND_FILL = 'linear-gradient(124.992deg,#E98245,#F0A06D 56%,#F6C5A7)';
export const SEND_GLYPH = '#332A25';
export const SEND_SHADOW = 'inset 0 1px 0 rgba(255,255,255,.42)';
/** Секундная подложка наведения у обычных кнопок раздела. */
export const BUTTON_HOVER = 'rgba(233,130,69,.1)';

/**
 * Счётчик непрочитанных — капсула из статьи 112689178: высота 24, растёт с
 * числом, потолок «99+»; цифры — акцентный токен на внутренней заливке кольца,
 * контур — фирменный градиент 1.5px. Точное число — в доступном имени строки.
 */
export const UNREAD_CAPSULE = { height: 24, minWidth: 24, padH: 7, radius: 12, contour: 1.5 } as const;
export function unreadLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * «…» действий у сообщения: видимый квадрат 28 (радиус 9, глиф 16) внутри зоны
 * нажатия 44; кольцо фокуса — на квадрате, 2px #E98245 с отступом 3 (global.css).
 */
export const MESSAGE_MENU = { hit: 44, face: 28, faceRadius: 9, glyph: 16, fadeMs: 120 } as const;
/** Тень палитры — единственная внешняя тень макета, у всплывающей панели. */
export const PICKER_SHADOW = '0 8px 30px rgba(0,0,0,.133)';

/**
 * Геометрия по раскладке (`chatsLayout`): wide — desktop v3, compact — сжатие
 * при 1100 px из того же CSS, phone — Mobile Web v1 (≤700 и телефон боком).
 */
export interface MessagesV3Geometry {
  railWidth: number;
  listWidth: number;
  gap: number;
  pad: { top: number; right: number; bottom: number; left: number };
  panelRadius: number;
  listPad: { v: number; h: number };
  searchHeight: number;
  searchFont: number;
  contactPad: { v: number; h: number };
  contactGap: number;
  contactMargin: number;
  contactAvatar: number;
  contactName: number;
  headerHeight: number;
  headerPadH: number;
  headerGap: number;
  headerAvatar: number;
  headerName: { size: number; line: number };
  conversationPadH: number | null;
  conversationPadV: number;
  conversationGap: number;
  contentMax: number;
  bubbleMax: string;
  bubblePad: { t: number; h: number; b: number };
  quotePadH: number;
  composeArea: { t: number; h: number; b: number };
  composerPad: number;
  composerGap: number;
  composerMinHeight: number;
  composerRadius: number;
  draftMaxHeight: number;
  draftPad: { v: number; h: number };
  composerButton: number;
  composerButtonGlyph: number;
  sendSize: { w: number; h: number; r: number };
}

export function messagesV3Geometry(layout: 'phone' | 'compact' | 'wide'): MessagesV3Geometry {
  const wide: MessagesV3Geometry = {
    railWidth: 78, listWidth: 330, gap: 14, pad: { top: 20, right: 24, bottom: 22, left: 12 }, panelRadius: 24,
    listPad: { v: 26, h: 14 }, searchHeight: 46, searchFont: 14, contactPad: { v: 16, h: 10 }, contactGap: 12,
    contactMargin: 6, contactAvatar: 42, contactName: 16,
    headerHeight: 88, headerPadH: 28, headerGap: 14, headerAvatar: 48, headerName: { size: 18, line: 28 },
    conversationPadH: null, conversationPadV: 22, conversationGap: 15, contentMax: 780,
    bubbleMax: '80%', bubblePad: { t: 14, h: 18, b: 10 }, quotePadH: 12,
    composeArea: { t: 14, h: 24, b: 22 }, composerPad: 12, composerGap: 7, composerMinHeight: 72, composerRadius: 20,
    // Поле: в макете `.draft` padding 7/12 при min-height 40, и строка стоит на
    // 1 px выше оси кнопок. CORE-1791 (возврат QA 23.10) — 10/12 при 44: та же
    // высота композера в покое (72), подсказка ровно на оси, а поле ввода не
    // меньше 44 по высоте, как остальные цели раздела. Рост — до тех же 100.
    draftMaxHeight: 100, draftPad: { v: 10, h: 12 }, composerButton: 40, composerButtonGlyph: 19,
    sendSize: { w: 46, h: 46, r: 14 },
  };
  if (layout === 'wide') return wide;
  if (layout === 'compact') {
    return {
      ...wide, railWidth: 66, listWidth: 280, gap: 10, pad: { top: 14, right: 14, bottom: 16, left: 8 },
      listPad: { v: 24, h: 10 }, contactName: 14, headerPadH: 24, conversationPadH: 20, conversationPadV: 20,
      bubbleMax: '92%', composeArea: { t: 14, h: 18, b: 20 }, composerGap: 3,
    };
  }
  return {
    ...wide, railWidth: 0, listWidth: 0, gap: 0, pad: { top: 12, right: 12, bottom: 12, left: 12 }, panelRadius: 22,
    listPad: { v: 22, h: 12 }, searchHeight: 48, searchFont: 16, contactPad: { v: 18, h: 10 }, contactMargin: 8,
    contactAvatar: 48, contactName: 16,
    headerHeight: 76, headerPadH: 12, headerGap: 9, headerAvatar: 40, headerName: { size: 16, line: 24 },
    conversationPadH: 14, conversationPadV: 20, conversationGap: 16,
    bubbleMax: '88%', bubblePad: { t: 12, h: 14, b: 9 }, quotePadH: 9,
    composeArea: { t: 10, h: 10, b: 12 }, composerPad: 7, composerGap: 1, composerMinHeight: 60, composerRadius: 18,
    draftMaxHeight: 120, draftPad: { v: 10, h: 7 }, composerButton: 44, composerButtonGlyph: 20,
    sendSize: { w: 42, h: 44, r: 13 },
  };
}

/**
 * Эмодзи комплекта в тексте раздела: 28 (ТЗ CORE-1791). Без пузыря — размеры
 * CORE-1730 (`EmojiOnlyMessage`: одна 72, две-три по 48), одни на весь веб.
 */
export const V3_EMOJI_INLINE = 28;

let backdropSupport: boolean | null = null;
/**
 * Есть ли `backdrop-filter`. Без него стекло на полупрозрачных RGBA читалось бы
 * как просвет, поэтому панели и поле получают непрозрачную основу (ТЗ: проверить
 * фолбэк без backdrop-filter).
 */
export function supportsBackdrop(): boolean {
  if (backdropSupport != null) return backdropSupport;
  if (Platform.OS !== 'web' || typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    backdropSupport = false;
    return backdropSupport;
  }
  backdropSupport = CSS.supports('backdrop-filter', 'blur(1px)') || CSS.supports('-webkit-backdrop-filter', 'blur(1px)');
  return backdropSupport;
}

/** Для тестов: сбросить кэш проверки. */
export function resetBackdropSupportForTests(value: boolean | null = null): void {
  backdropSupport = value;
}

/**
 * Стекло: размытие фона под слоем (не текста — у контейнера opacity 1). При
 * отсутствии поддержки — непрозрачная основа под теми же слоями.
 */
export function glass(blur: number, fallbackBase: string, saturate = true): Record<string, unknown> {
  if (Platform.OS !== 'web') return {};
  const filter = `blur(${blur}px)${saturate ? ' saturate(120%)' : ''}`;
  return supportsBackdrop()
    ? { backdropFilter: filter, WebkitBackdropFilter: filter }
    : { backgroundColor: fallbackBase };
}

/** Переходы макета: фон 160ms, сдвиг 120ms; reduced-motion их выключает. */
export function v3Transition(reduceMotion: boolean): Record<string, unknown> {
  if (Platform.OS !== 'web' || reduceMotion) return {};
  return { transitionProperty: 'background-color, background-image, box-shadow, transform, filter', transitionDuration: '160ms, 160ms, 160ms, 120ms, 160ms', transitionTimingFunction: 'ease' };
}

/** Начальные буквы имени для аватара без фото: «Мастер Агапелла» → «МА». */
export function initialsOf(name: string | null | undefined): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = Array.from(words[0])[0] ?? '';
  const second = words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? '') : '';
  return (first + second).toUpperCase();
}

/**
 * Тон подложки аватара без фото: стабильно по имени из тёплых нейтральных
 * тонов макета (`.a1`–`.a3` и основной), чтобы список не был одноцветным.
 */
const AVATAR_TINTS = ['rgba(215,183,164,.13)', 'rgba(158,174,155,.14)', 'rgba(210,176,140,.145)', 'rgba(169,158,181,.137)'];
export function avatarTintOf(name: string | null | undefined): string {
  let h = 0;
  for (const ch of name || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
}
